import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/ErrorState'
import type { useMyTasks } from '@/features/tasks/api/useMyTasks'
import { MyTaskSectionCard } from '@/features/tasks/components/MyTaskSectionCard'
import type { MyTaskSection } from '@/types/domain'

/** How many rows a section shows in the strip; the rest are counted, and «Все
 *  задачи» opens the page that lists them. */
const STRIP_ROWS = 5

/** The sections the strip draws: the served ones with something in them, in
 *  the served order. NO capability filter here — which sections a reader holds
 *  is the server's answer, so Super-admin, who holds all eight, sees every one
 *  that is not empty. */
function stripSections(sections: MyTaskSection[]): MyTaskSection[] {
  return sections.filter((section) => section.count > 0)
}

/* «Мои задачи» above the dashboard (S-2b.2, owner decisions 2026-10-10 and Q6).
 *
 * Finance and Super-admin keep the dashboard as their home, so their tasks come
 * to it: the strip sits between the greeting and the counters and holds the
 * rate approvals and the payment-confirmation queue that used to be rows of
 * «Требует решения», beside whatever else the reader holds. «К оплате» is not
 * here — the counter below already says it.
 *
 * It takes the page's `useMyTasks()` result rather than calling the hook, so
 * the page and the subtitle read one query. While it loads it renders nothing
 * (the dashboard has its own skeleton, and a second one above it would jump),
 * and with every section empty it renders nothing too: there is nothing to do.
 * A failed read is said in place with a retry, and the dashboard below it still
 * renders — the two reads fail independently. */
export function MyTasksStrip({ tasks }: { tasks: ReturnType<typeof useMyTasks> }) {
  const { t } = useTranslation()

  if (tasks.isError) {
    return (
      <section aria-label={t('myTasks.title')}>
        <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />
      </section>
    )
  }

  const sections = tasks.data ? stripSections(tasks.data.sections) : []
  if (sections.length === 0) return null

  return (
    <section aria-label={t('myTasks.title')} className="grid items-start gap-3.5 lg:grid-cols-2">
      {sections.map((section) => (
        <MyTaskSectionCard
          key={section.key}
          section={section}
          limit={STRIP_ROWS}
          linkAllTasks
        />
      ))}
    </section>
  )
}
